import { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { AlertTriangle } from 'lucide-react';
import InfoTip from '@/components/InfoTip';
import StreakRiskChartModal from '@/components/StreakRiskChartModal';
import SwipeHint, { useSwipeHint } from '@/components/SwipeHint';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TIER_META = {
  king:       { emoji: '\u{1F451}', label: 'King' },
  aristocrat: { emoji: '\u{1F3C6}', label: 'Aristocrat' },
  achiever:   { emoji: '\u{1F396}\uFE0F', label: 'Achiever' },
};

/**
 * StreakAtRiskRow — companion to StreakHighlightRow.
 *
 * Surfaces King / Aristocrat / Achiever stocks whose most-recent
 * full-year dividend amount didn't grow vs the prior year (yellow
 * flag — the multi-decade raise streak may be at risk). Pulls from
 * the conservative `/screener/streaks-at-risk` endpoint which only
 * looks at finalized historical years, so signal here is real
 * (companies that genuinely had a non-growth year), not noise.
 *
 * Renders nothing when no at-risk K/A/A exist OR when none of the
 * surfaced tickers are owned by the user (we don't want to spam
 * generic "watch out!" lists — only highlight risks the user has
 * skin in OR offer them as discoverable). Toggle: showAll prop.
 *
 * Dashboard usage:
 *   <StreakAtRiskRow onStockClick={handleStockClick} ownedTickers={ownedTickers} />
 */
export default function StreakAtRiskRow({ onStockClick, ownedTickers = [], tierFilter = null }) {
  const [items, setItems] = useState(null);
  const swipeRef = useRef(null);
  const [swipeHint, updateSwipeHint] = useSwipeHint(swipeRef, [tierFilter, items]);
  // Track which ticker the user clicked on so we can open the
  // chart-first modal. Stores the full row meta so the modal can
  // render the YoY pill without an extra round-trip.
  const [activeRisk, setActiveRisk] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/screener/streaks-at-risk`);
        if (cancelled) return;
        const raw = Array.isArray(res.data?.items) ? res.data.items : [];
        const filtered = tierFilter
          ? raw.filter((it) => (it.dividend_tier || '').toLowerCase() === String(tierFilter).toLowerCase())
          : raw;
        setItems(filtered);
      } catch {
        if (!cancelled) setItems([]);
      }
    })();
    return () => { cancelled = true; };
  }, [tierFilter]);

  if (!items || items.length === 0) return null;

  const ownedSet = new Set((ownedTickers || []).map((t) => String(t).toUpperCase()));

  return (
    <div
      className="mb-3 rounded-lg border border-yellow-400/40 bg-gradient-to-r from-yellow-50/70 via-amber-50/40 to-transparent dark:from-yellow-950/25 dark:via-amber-950/15 dark:to-transparent px-3 py-2 shadow-[0_4px_12px_-2px_rgba(15,23,42,0.14),0_2px_4px_-1px_rgba(15,23,42,0.06)] dark:shadow-[0_1px_0_0_rgba(255,255,255,0.04)_inset,0_2px_8px_-2px_rgba(0,0,0,0.6)]"
      data-testid="streak-at-risk-row"
    >
      <div className="flex items-center gap-2 mb-1.5 flex-wrap">
        <AlertTriangle className="w-3.5 h-3.5 text-yellow-600 dark:text-yellow-400 shrink-0" />
        <span className="text-[11px] font-bold uppercase tracking-wider text-yellow-900 dark:text-yellow-200">
          Streaks At Risk
        </span>
        <span className="text-[11px] text-yellow-700 dark:text-yellow-300/80">
          This year's payouts at the current rate won't top last year — a raise is still needed to keep the streak
        </span>
        <InfoTip
          content="Kings/Aristocrats/Achievers whose most-recent finalized year-over-year dividend didn't show meaningful growth (≤ 0.5% change). Yellow flag, not alarm — many will recover. Click any chip to open the Dividend Growth tab and see the full history."
          maxWidth={300}
        />
      </div>

      <div ref={swipeRef} onScroll={updateSwipeHint} className="flex items-stretch gap-1.5 overflow-x-auto pb-1 -mx-1 px-1 scrollbar-thin">
        {items.map((s) => {
          const meta = TIER_META[(s.dividend_tier || '').toLowerCase()] || { emoji: '', label: '' };
          const yoy = Number(s.yoy_change_pct);
          const yoyStr = yoy === 0 ? 'flat' : `${yoy > 0 ? '+' : ''}${yoy.toFixed(2)}%`;
          const streak = s.dividend_streak_years
            ? ` · ${s.dividend_streak_years}y streak`
            : '';
          const tooltip = `${s.name || s.ticker}: ${meta.label}${streak} — ${s.prior_year} $${Number(s.prior_amount).toFixed(2)} → ${s.last_year} projected $${Number(s.last_amount).toFixed(2)} (${yoyStr} YoY)${s.reason ? ` · ${s.reason}` : ''}`;
          const isOwned = ownedSet.has(String(s.ticker).toUpperCase());
          return (
            <button
              key={s.ticker}
              type="button"
              onClick={() => setActiveRisk(s)}
              title={tooltip}
              data-testid={`at-risk-chip-${s.ticker}`}
              data-owned={isOwned ? 'true' : 'false'}
              className={`shrink-0 flex flex-col items-start gap-0.5 px-2 py-1 rounded-md transition-colors ${
                isOwned
                  ? 'bg-emerald-50 dark:bg-emerald-950/40 hover:bg-emerald-100 dark:hover:bg-emerald-950/60 border border-emerald-400/70 dark:border-emerald-600/60 ring-1 ring-emerald-400/30'
                  : 'bg-white/70 dark:bg-zinc-900/70 hover:bg-yellow-100 dark:hover:bg-yellow-950/60 border border-yellow-300/60 dark:border-yellow-700/50'
              }`}
            >
              <span className="inline-flex items-center gap-1 text-[11px] font-semibold">
                <span className="text-[11px]" aria-hidden>{meta.emoji}</span>
                <span className="font-mono font-bold text-foreground">{s.ticker}</span>
                <span className={`inline-flex items-center gap-0.5 ${yoy < 0 ? 'text-rose-700 dark:text-rose-300' : 'text-yellow-700 dark:text-yellow-400'}`}>
                  {yoyStr}
                </span>
              </span>
              <span className="text-[10px] font-mono text-muted-foreground/90 leading-none">
                {s.prior_year} → {s.last_year}
              </span>
            </button>
          );
        })}
      </div>
      <SwipeHint hint={swipeHint} testId="streak-at-risk-swipe-hint" />

      <StreakRiskChartModal
        open={!!activeRisk}
        onOpenChange={(o) => { if (!o) setActiveRisk(null); }}
        ticker={activeRisk?.ticker}
        riskMeta={activeRisk}
        onOpenFullStock={(tkr) => onStockClick && onStockClick(tkr)}
      />
    </div>
  );
}
